import { lazy, Suspense, useEffect } from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import PageTransition from "@/components/PageTransition";
import { HelmetProvider } from "react-helmet-async";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import CustomCodeInjector from "@/components/CustomCodeInjector";
import { AuthProvider } from "@/hooks/useAuth";
import { LanguageProvider } from "@/hooks/useLanguage";
import { CartProvider } from "@/hooks/useCart";
import { AdminEditProvider } from "@/contexts/AdminEditContext";
import AdminEditToggle from "@/components/AdminEditToggle";
import ErrorBoundary from "@/components/ErrorBoundary";
import CartSheet from "@/components/CartSheet";
import SharedCartLoader from "@/components/SharedCartLoader";
import { useBrevoSync } from "@/hooks/useBrevoSync";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import GlobalJsonLd from "@/components/GlobalJsonLd";
import AuthGuard from "@/components/AuthGuard";
import Navbar from "@/components/Navbar";
import MobileBottomNav from "@/components/MobileBottomNav";
import Breadcrumbs from "@/components/Breadcrumbs";
import EmailVerificationBanner from "@/components/EmailVerificationBanner";
import Index from "./pages/Index.tsx";

// Deferred non-critical components
const AccessibilityWidget = lazy(() => import("@/components/AccessibilityWidget"));
const WebMCPTools = lazy(() => import("@/components/WebMCPTools"));
const CookieConsent = lazy(() => import("@/components/CookieConsent"));

const AboutUsTab = lazy(() => import("@/components/AboutUsTab"));
const LanguageSideTab = lazy(() => import("@/components/LanguageSideTab"));
const Footer = lazy(() => import("@/components/Footer"));

// Lazy-loaded pages
const ProductDetail = lazy(() => import("./pages/ProductDetail.tsx"));
const Auth = lazy(() => import("./pages/Auth.tsx"));
const ResetPassword = lazy(() => import("./pages/ResetPassword.tsx"));
const BecomeVendor = lazy(() => import("./pages/BecomeVendor.tsx"));
const VendorInfo = lazy(() => import("./pages/VendorInfo.tsx"));
const WhyVendor = lazy(() => import("./pages/WhyVendor.tsx"));
const VendorPayouts = lazy(() => import("./pages/VendorPayouts.tsx"));
const VendorAgreement = lazy(() => import("./pages/VendorAgreement.tsx"));
const VendorApplicationStatus = lazy(() => import("./pages/VendorApplicationStatus.tsx"));
const Profile = lazy(() => import("./pages/Profile.tsx"));
const MyOrders = lazy(() => import("./pages/MyOrders.tsx"));
const OrderStatus = lazy(() => import("./pages/OrderStatus.tsx"));
const VendorDashboard = lazy(() => import("./pages/VendorDashboard.tsx"));
const VendorStore = lazy(() => import("./pages/VendorStore.tsx"));
const AffiliateDashboard = lazy(() => import("./pages/AffiliateDashboard.tsx"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard.tsx"));
const Checkout = lazy(() => import("./pages/Checkout.tsx"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation.tsx"));
const NotFound = lazy(() => import("./pages/NotFound.tsx"));
const Categories = lazy(() => import("./pages/Categories.tsx"));
const CategoryPage = lazy(() => import("./pages/CategoryPage.tsx"));
const NewArrivals = lazy(() => import("./pages/NewArrivals.tsx"));
const Sales = lazy(() => import("./pages/Sales.tsx"));
const DailyDeals = lazy(() => import("./pages/DailyDeals.tsx"));
const Coupons = lazy(() => import("./pages/Coupons.tsx"));
const HelpCenter = lazy(() => import("./pages/HelpCenter.tsx"));
const ReturnsPolicy = lazy(() => import("./pages/ReturnsPolicy.tsx"));
const PaymentPolicy = lazy(() => import("./pages/PaymentPolicy.tsx"));
const Shipping = lazy(() => import("./pages/Shipping.tsx"));
const Contact = lazy(() => import("./pages/Contact.tsx"));
const AccessibilityStatement = lazy(() => import("./pages/AccessibilityStatement.tsx"));
const Wishlist = lazy(() => import("./pages/Wishlist.tsx"));
const SharedWishlist = lazy(() => import("./pages/SharedWishlist.tsx"));
const MyReviews = lazy(() => import("./pages/MyReviews.tsx"));
const SettingsPage = lazy(() => import("./pages/Settings.tsx"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy.tsx"));
const LegalInfo = lazy(() => import("./pages/LegalInfo.tsx"));
const Blog = lazy(() => import("./pages/Blog.tsx"));
const BlogPost = lazy(() => import("./pages/BlogPost.tsx"));
const PointsHistory = lazy(() => import("./pages/PointsHistory.tsx"));
const About = lazy(() => import("./pages/About.tsx"));
const Partners = lazy(() => import("./pages/Partners.tsx"));
const BusinessGifts = lazy(() => import("./pages/BusinessGifts.tsx"));
const PaymentResult = lazy(() => import("./pages/PaymentResult.tsx"));
const SearchResults = lazy(() => import("./pages/SearchResults.tsx"));
const SharedCart = lazy(() => import("./pages/SharedCart.tsx"));
const GiftCards = lazy(() => import("./pages/GiftCards.tsx"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe.tsx"));
const InstallApp = lazy(() => import("./pages/InstallApp.tsx"));

const queryClient = new QueryClient();

const STANDALONE_ROUTES = ["/ניהול"];

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo({ top: 0, left: 0, behavior: "instant" }); }, [pathname]);
  return null;
};

const BrevoSyncProvider = () => {
  useBrevoSync();
  return null;
};

const PushNotificationProvider = () => {
  usePushNotifications();
  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  const isStandalone = STANDALONE_ROUTES.some((r) => location.pathname === r || location.pathname.startsWith(r + "/"));

  return (
    <>
      <ScrollToTop />
      <GlobalJsonLd />
      <Suspense fallback={null}><WebMCPTools /></Suspense>
      {!isStandalone && <Navbar />}
      <EmailVerificationBanner />
      {!isStandalone && <Breadcrumbs />}
      <div className="overflow-x-hidden">
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
          <AnimatePresence mode="wait" initial={false}>
            <PageTransition key={location.pathname}>
              <Routes location={location}>
                <Route path="/" element={<Index />} />
                <Route path="/product/:slug" element={<ProductDetail />} />
                <Route path="/מוצר/:slug" element={<ProductDetail />} />
                <Route path="/כניסה" element={<Auth />} />
                <Route path="/איפוס-סיסמה" element={<ResetPassword />} />
                <Route path="/הפוך-לספק" element={<BecomeVendor />} />
                <Route path="/מידע-לספקים" element={<VendorInfo />} />
                <Route path="/מכירה-באתר" element={<VendorInfo />} />
                <Route path="/למה-כדאי-להצטרף-כספק" element={<WhyVendor />} />
                <Route path="/איך-מקבלים-תשלום" element={<VendorPayouts />} />
                <Route path="/הסכם-ספקים" element={<VendorAgreement />} />
                <Route path="/סטטוס-בקשת-ספק" element={<VendorApplicationStatus />} />
                <Route path="/פורטל-ספקים" element={<AuthGuard requireRole="vendor"><VendorDashboard /></AuthGuard>} />
                <Route path="/חנות/:vendorId" element={<VendorStore />} />
                <Route path="/שותפים" element={<AuthGuard><AffiliateDashboard /></AuthGuard>} />
                <Route path="/ניהול" element={<AuthGuard requireRole={["admin", "moderator", "vendor", "product_editor"]}><AdminDashboard /></AuthGuard>} />
                <Route path="/הזמנות" element={<AuthGuard><MyOrders /></AuthGuard>} />
                <Route path="/סטטוס-הזמנה/:orderId" element={<AuthGuard><OrderStatus /></AuthGuard>} />

                <Route path="/תשלום" element={<Checkout />} />
                <Route path="/אישור-הזמנה/:orderId" element={<OrderConfirmation />} />
                <Route path="/תוצאת-תשלום" element={<PaymentResult />} />
                <Route path="/פרופיל" element={<AuthGuard><Profile /></AuthGuard>} />
                <Route path="/נקודות" element={<AuthGuard><PointsHistory /></AuthGuard>} />
                <Route path="/מועדפים" element={<AuthGuard><Wishlist /></AuthGuard>} />
                <Route path="/רשימת-מתנות/:shareCode" element={<SharedWishlist />} />
                <Route path="/הביקורות-שלי" element={<AuthGuard><MyReviews /></AuthGuard>} />
                <Route path="/קטגוריות" element={<Categories />} />
                <Route path="/קטגוריה/:categorySlug" element={<CategoryPage />} />
                <Route path="/קטגוריה/:categorySlug/:subCategory" element={<CategoryPage />} />
                <Route path="/מוצרים-חדשים" element={<NewArrivals />} />
                <Route path="/חיפוש" element={<SearchResults />} />
                <Route path="/מבצעים" element={<Sales />} />
                <Route path="/הנחות-יומיות" element={<DailyDeals />} />
                <Route path="/קופונים" element={<Coupons />} />
                <Route path="/מרכז-עזרה" element={<HelpCenter />} />
                <Route path="/מדיניות-החזרות" element={<ReturnsPolicy />} />
                <Route path="/מדיניות-תשלום" element={<PaymentPolicy />} />
                <Route path="/משלוחים" element={<Shipping />} />
                <Route path="/צור-קשר" element={<Contact />} />
                <Route path="/הצהרת-נגישות" element={<AccessibilityStatement />} />
                <Route path="/הגדרות" element={<SettingsPage />} />
                <Route path="/מדיניות-פרטיות" element={<PrivacyPolicy />} />
                <Route path="/מידע-משפטי" element={<LegalInfo />} />
                <Route path="/בלוג" element={<Blog />} />
                <Route path="/בלוג/:slug" element={<BlogPost />} />
                <Route path="/אודות" element={<About />} />
                <Route path="/שותפים-שלנו" element={<Partners />} />
                <Route path="/מתנות-לעסקים" element={<BusinessGifts />} />
                <Route path="/סל-משותף/:shareCode" element={<SharedCart />} />
                <Route path="/גיפט-קארד" element={<GiftCards />} />
                <Route path="/unsubscribe" element={<Unsubscribe />} />
                <Route path="/התקנה" element={<InstallApp />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </PageTransition>
          </AnimatePresence>
        </Suspense>
        </div>
      {!isStandalone && <Suspense fallback={null}><Footer /></Suspense>}
      {!isStandalone && <MobileBottomNav />}
      {/* Floating promo overlays (vendor banner, social proof popups) are removed:
          Merchant Center requires browsing and checkout free of interruptions. */}
      <Suspense fallback={null}>
        <AccessibilityWidget />
        <LanguageSideTab />
        <CookieConsent />
        <AboutUsTab />
      </Suspense>
      <AdminEditToggle />
      {!isStandalone && <div className="h-16 md:hidden" />}
    </>
  );
};

const App = () => {
  return (
    <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LanguageProvider>
          <CartProvider>
            <AdminEditProvider>
            <TooltipProvider>
              <CustomCodeInjector />
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <BrevoSyncProvider />
                <PushNotificationProvider />
                <SharedCartLoader />
                <CartSheet />
                <AnimatedRoutes />
              </BrowserRouter>
            </TooltipProvider>
            </AdminEditProvider>
          </CartProvider>
          </LanguageProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;

